import React from "react";
import { PhoneCall, MessageCircle, Lock } from "lucide-react";
import { getPublicContactAction } from "../../../lib/contactRouting";
import { useConfig } from "../../../lib/ConfigContext";

/**
 * v2.70 Phase 4 — Sticky mobile bottom bar.
 * v2.71.67 — Enquiry slot REMOVED. Only WhatsApp + Request Callback remain.
 * v2.87.x  — WhatsApp button now routes via `getPublicContactAction()`:
 *            anonymous → login wall, client/non-owner → HouseEntry official
 *            number with pre-filled inventory-aware message, owner →
 *            their own number, internal roles → bar hidden.
 *
 * Hidden on lg+ (desktop has its own action bar) and when `disabled`
 * (internal roles or Sold properties).
 */
export default function MobileBottomBar({
  onCallback,
  onLoginRequired,
  user,
  property,
  disabled,
}) {
  const { content } = useConfig();
  if (disabled) return null;

  const currentUrl = typeof window !== "undefined" ? window.location.href : "";
  const action = getPublicContactAction(property, user, content, currentUrl);
  // Internal operators view → hide the bar entirely.
  if (action.mode === "internal_hidden") return null;
  const showLoginCta = action.mode === "login_required";

  return (
    <div
      className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-[#E4E4E7] shadow-[0_-4px_12px_rgba(0,0,0,0.06)] px-3 py-2.5 flex items-center gap-2"
      data-testid="mobile-bottom-bar"
    >
      {showLoginCta ? (
        // v2.87.x — When the viewer is anon, the SOLE CTA is the login
        // wall. We intentionally do NOT render Request Callback alongside
        // it because the spec is explicit: "public users trigger login
        // wall" — no contact CTAs of any kind should be reachable until
        // the user authenticates.
        <button
          type="button"
          onClick={onLoginRequired}
          className="flex-1 inline-flex items-center justify-center gap-1.5 h-11 rounded-xl bg-[#0055FF] hover:bg-[#0044CC] text-white font-semibold text-[13px] transition-all active:scale-[0.98]"
          data-testid="mobile-bar-login-required"
        >
          <Lock size={14} /> {action.buttonLabel}
        </button>
      ) : (
        <>
          {action.whatsappHref ? (
            <a
              href={action.whatsappHref}
              target="_blank"
              rel="noreferrer"
              className="flex-1 inline-flex items-center justify-center gap-1.5 h-11 rounded-xl bg-[#25D366] hover:bg-[#1FB855] text-white font-semibold text-[13px] transition-all active:scale-[0.98]"
              data-testid="mobile-bar-whatsapp"
            >
              <MessageCircle size={15} /> {action.buttonLabel}
            </a>
          ) : null}
          <button
            onClick={onCallback}
            className="flex-1 inline-flex items-center justify-center gap-1.5 h-11 rounded-xl bg-[#0055FF] hover:bg-[#0044CC] text-white font-semibold text-[13px] transition-all active:scale-[0.98]"
            data-testid="mobile-bar-callback"
          >
            <PhoneCall size={15} /> Request Callback
          </button>
        </>
      )}
    </div>
  );
}
